import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDate, fmtTime, payLabel } from "@/lib/api";
import { StatusPill } from "@/components/StatusPill";
import { Plus, Users } from "lucide-react";

const Stat = ({ label, value, testId }) => (
  <div className="card-surface p-5" data-testid={testId}>
    <p className="overline">{label}</p>
    <p className="mt-2 font-display text-3xl font-semibold">{value}</p>
  </div>
);

export default function PharmacyDashboard() {
  const [summary, setSummary] = useState(null);
  const [shifts, setShifts] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, sh] = await Promise.all([api.get("/pharmacy/summary"), api.get("/pharmacy/shifts")]);
      setSummary(s.data);
      setShifts(sh.data);
    })();
  }, []);

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = shifts.filter((s) => s.date >= today && s.status !== "Cancelled");

  return (
    <div data-testid="pharmacy-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline">Dashboard</p>
          <h1 className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl">Upcoming coverage</h1>
        </div>
        <Link to="/pharmacy/post" className="btn-primary" data-testid="dashboard-post-shift">
          <Plus className="h-4 w-4" /> Post shift
        </Link>
      </div>

      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Open shifts" value={summary?.open_shifts ?? "—"} testId="stat-open" />
        <Stat label="Confirmed" value={summary?.confirmed_shifts ?? "—"} testId="stat-confirmed" />
        <Stat label="Workers interested" value={summary?.workers_interested ?? "—"} testId="stat-interested" />
        <Stat label="Upcoming shifts" value={summary?.upcoming_shifts ?? "—"} testId="stat-upcoming" />
      </div>

      {upcoming.length === 0 ? (
        <div className="card-surface mt-10 p-8" data-testid="dashboard-empty">
          <p className="text-sm text-slate-600">No upcoming coverage yet. Post your first shift — it takes under a minute.</p>
          <Link to="/pharmacy/post" className="btn-primary mt-5">
            Post a shift
          </Link>
        </div>
      ) : (
        <div className="card-surface mt-10 overflow-x-auto">
          <table className="w-full min-w-[820px] text-sm">
            <thead className="border-b border-slate-200 bg-slate-50/60 text-left">
              <tr className="[&>th]:px-5 [&>th]:py-3">
                <th className="overline">Date</th>
                <th className="overline">Role</th>
                <th className="overline">Location</th>
                <th className="overline">Hours</th>
                <th className="overline">Pay</th>
                <th className="overline">Matches</th>
                <th className="overline">Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {upcoming.map((s) => (
                <tr key={s.shift_id} className="border-b border-slate-100 last:border-0" data-testid={`dashboard-row-${s.shift_id}`}>
                  <td className="px-5 py-4 font-medium">{fmtDate(s.date, { weekday: "short", month: "short", day: "numeric" })}</td>
                  <td className="px-5 py-4">{s.worker_role}</td>
                  <td className="px-5 py-4 text-slate-600">{s.city}</td>
                  <td className="px-5 py-4">{fmtTime(s.start_time)} – {fmtTime(s.end_time)}</td>
                  <td className="px-5 py-4 font-display font-semibold">{payLabel(s)}</td>
                  <td className="px-5 py-4">
                    <span className="flex items-center gap-1.5 text-slate-600">
                      <Users className="h-4 w-4 text-slate-400" /> {s.interest_count}
                    </span>
                  </td>
                  <td className="px-5 py-4"><StatusPill status={s.status} /></td>
                  <td className="px-5 py-4">
                    <Link to={`/pharmacy/shifts/${s.shift_id}`} className="text-sm font-medium text-brand" data-testid={`dashboard-matches-${s.shift_id}`}>
                      View matches
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
